import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { Button } from '@interlayer/js/components/common/Button';

import { useTranslations } from '@core/js/hooks/i18n';
import { storageService } from '@core/js/services/storage.service';
import { useDeviceSelector } from '@core/js/store/hooks';

import { useMediaQuery } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useQueryParam } from '@interlayer/js/app/hooks/useQueryParam';
import { QUERY_PARAMS } from '@interlayer/js/app/hooks/useQueryParam/constants';
import { setIsDownloadAppOpen } from '@interlayer/js/components/system/App/store/buzz/actions';
import {
    INBOX_CONTAINER
} from '@interlayer/js/components/system/CommunicationWidgets/hooks/useInitWidgets/constants.buzz';

import { useDownloadApp } from './hooks/useDownloadApp';
import { DOWNLOAD_APP_LINK, STATE_DOWNLOAD_APP_PROMPT } from './constants';

import style from './DownloadApp.scss';

export const DownloadApp: React.FC = () => {
    const dispatch = useDispatch();
    const redirectFromTabs = useQueryParam(QUERY_PARAMS.redirectFromTabs);
    const os = useDeviceSelector(prop('os'));
    const isDownloadApp = useDownloadApp(true);
    const [isShow, setIsShow] = useState(true);
    const t = useTranslations(`playtech.mobile.system.downloadapp.${os}.`);

    const isLogo = useMediaQuery('(min-width: 401px)');

    useEffect(() => {
        const handlerShowAppLink = () => {
            if (!isDownloadApp) {
                return;
            }

            const downloadAppLink = storageService.local.getItem(DOWNLOAD_APP_LINK);

            if (!downloadAppLink) {
                storageService.local.setItem(DOWNLOAD_APP_LINK, STATE_DOWNLOAD_APP_PROMPT.NEED_TO_SHOW);
                return;
            }

            if (downloadAppLink === STATE_DOWNLOAD_APP_PROMPT.NEED_TO_SHOW) {
                storageService.local.setItem(DOWNLOAD_APP_LINK, STATE_DOWNLOAD_APP_PROMPT.IS_HIDDEN);
            }
        };

        window?.addEventListener(os === 'ios' ? 'pagehide' : 'beforeunload', handlerShowAppLink);

        return () => {
            window.removeEventListener('beforeunload', handlerShowAppLink);
            window.removeEventListener('pagehide', handlerShowAppLink);
        };
    }, [isDownloadApp, os]);

    const isDownloadAppStatus = !isDownloadApp
        || !isShow
        || storageService.local.getItem(DOWNLOAD_APP_LINK) !== STATE_DOWNLOAD_APP_PROMPT.NEED_TO_SHOW
        || redirectFromTabs;

    useEffect(() => {
        dispatch(setIsDownloadAppOpen(!isDownloadAppStatus));
    }, [dispatch, isDownloadAppStatus]);

    if (isDownloadAppStatus) {
        return null;
    }

    const handlerClose = () => {
        storageService.local.setItem(DOWNLOAD_APP_LINK, STATE_DOWNLOAD_APP_PROMPT.IS_HIDDEN);
        setIsShow(false);
        document.getElementById(INBOX_CONTAINER)?.classList.remove('isDownloadAppOpen');
    };

    return (
        <div className={style.downloadApp}>
            <SvgIcon
                type="close-popup"
                className={style.close}
                onClick={handlerClose}
            />

            {isLogo && <div className={style.logo} />}

            <div className={style.infoWrap}>
                <div className={style.title}>{t('title')}</div>

                <div className={style.description}>{t('description')}</div>

                <div className={style.text}>{t('text')}</div>
            </div>

            <Button href={t('link')}>{t('ctalabel')}</Button>
        </div>
    );
};

